import { getVoiceURI } from 'modules/sdk/lib/utils/getVoiceURI';

import { localVoicesBlacklist } from './localVoicesBlacklist';

export const dedupeLocalVoices = (voices: SpeechSynthesisVoice[]) => {
  const voiceMap = new Map<string, SpeechSynthesisVoice>();

  voices.forEach(voice => {
    if (!voiceMap.has(getVoiceURI(voice))) {
      voiceMap.set(getVoiceURI(voice), voice);
    }
  });

  return Array.from(voiceMap.values());
};

const filterBlacklistedLocalVoices = (voices: SpeechSynthesisVoice[]) => {
  return voices.filter(voice => !localVoicesBlacklist.some(blacklistedVoice => voice.name.startsWith(blacklistedVoice)));
};

export const getLocalVoicePromise = (): Promise<SpeechSynthesisVoice[]> => {
  if (typeof window === 'undefined' || !window.speechSynthesis) {
    console.warn('Speech Synthesis API is not available');
    return Promise.resolve([]);
  }

  const TIMEOUT_MS = 3000;

  const processVoices = (voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice[] => {
    try {
      const uniqueVoices = dedupeLocalVoices(voices);
      const filteredVoices = filterBlacklistedLocalVoices(uniqueVoices);
      return filteredVoices;
    } catch (error) {
      console.error('Error processing voices:', error);
      return [];
    }
  };

  return new Promise<SpeechSynthesisVoice[]>(resolve => {
    let resolved = false;
    let timeoutId: NodeJS.Timeout | undefined;
    let voicesChangedHandler: (() => void) | undefined;

    // Cleanup function to prevent memory leaks
    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = undefined;
      }
      if (voicesChangedHandler) {
        speechSynthesis.removeEventListener('voiceschanged', voicesChangedHandler);
        voicesChangedHandler = undefined;
      }
    };

    // Safe resolve function that ensures we only resolve once
    const safeResolve = (voices: SpeechSynthesisVoice[]) => {
      if (!resolved) {
        resolved = true;
        cleanup();
        resolve(voices);
      }
    };

    try {
      // Try to get voices immediately
      const immediateVoices = speechSynthesis.getVoices();
      const processedVoices = processVoices(immediateVoices);

      if (processedVoices.length > 0) {
        safeResolve(processedVoices);
        return;
      }

      // Set up timeout
      timeoutId = setTimeout(() => {
        console.warn(`Voice loading timed out after ${TIMEOUT_MS}ms`);
        safeResolve([]);
      }, TIMEOUT_MS);

      // Set up voice change listener
      voicesChangedHandler = () => {
        try {
          const voices = speechSynthesis.getVoices();
          const processedVoices = processVoices(voices);

          // Only resolve if we actually got voices
          if (processedVoices.length > 0) {
            safeResolve(processedVoices);
          }
          // If still no voices, let the timeout handle it
        } catch (error) {
          console.error('Error in voiceschanged handler:', error);
          // Don't resolve with empty array here, let timeout handle it
        }
      };

      speechSynthesis.addEventListener('voiceschanged', voicesChangedHandler);

      // Some browsers might have already loaded voices but didn't trigger the event
      // Double-check after a micro-task
      Promise.resolve().then(() => {
        if (!resolved) {
          const voices = speechSynthesis.getVoices();
          const processedVoices = processVoices(voices);
          if (processedVoices.length > 0) {
            safeResolve(processedVoices);
          }
        }
      });
    } catch (error) {
      console.error('Error in getLocalVoicePromise:', error);
      cleanup();
      resolve([]); // Resolve with empty array rather than rejecting
    }
  });
};

export const getLocalVoicePromiseWithRetry = async (maxRetries = 3, retryDelay = 1000): Promise<SpeechSynthesisVoice[]> => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const voices = await getLocalVoicePromise();

      if (voices.length > 0) {
        return voices;
      }

      if (attempt < maxRetries) {
        console.error(`No voices found, retrying... (attempt ${attempt}/${maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
    } catch (error) {
      console.error(`Attempt ${attempt} failed:`, error);
      if (attempt === maxRetries) {
        throw error;
      }
    }
  }

  return [];
};
